import { useCallback, useState } from 'react'

import { americanToDecimal, combinedDecimalOdds, formatAmericanOdds } from '../../utils/odds'
import { EMPTY_AI_PROMPT_FIELDS, normalizeAiPromptFields } from './imagePrompt'
import { canResumeImageGenerationJob } from './imageGenerationJob'

const PREDICTION_PROVIDERS = new Set(['cryptodotcom', 'og'])

export const KAMBI_WAGER_TYPES = [
  { value: 'single', label: 'Single', kind: 'single', couponType: 'single', outcomes: 1 },
  { value: 'multiple_singles', label: 'Multiple singles', kind: 'single', couponType: 'single', minimumOutcomes: 2 },
  { value: 'combination', label: 'Parlay', kind: 'parlay', couponType: 'combination', minimumOutcomes: 2 },
  { value: 'same_game_parlay', label: 'Same game parlay', kind: 'same_game_parlay', couponType: 'combination', minimumOutcomes: 2 },
  { value: 'system', label: 'System', kind: 'system', couponType: 'system', minimumOutcomes: 2 },
  { value: 'patent', label: 'Patent', kind: 'system', couponType: 'patent', outcomes: 3 },
  { value: 'trixie', label: 'Trixie', kind: 'system', couponType: 'trixie', outcomes: 3 },
  { value: 'yankee', label: 'Yankee', kind: 'system', couponType: 'yankee', outcomes: 4 },
  { value: 'canadian', label: 'Canadian', kind: 'system', couponType: 'canadian', outcomes: 5 },
  { value: 'heinz', label: 'Heinz', kind: 'system', couponType: 'heinz', outcomes: 6 },
  { value: 'superheinz', label: 'Super Heinz', kind: 'system', couponType: 'superheinz', outcomes: 7 },
]

function wagerType(value) {
  return KAMBI_WAGER_TYPES.find((option) => option.value === value) || KAMBI_WAGER_TYPES[0]
}

export function authoredWagerType(entry) {
  if (entry?.kind === 'system') return entry.coupon_type || 'system'
  if (entry?.kind === 'same_game_parlay') return 'same_game_parlay'
  if (entry?.kind === 'parlay') return 'combination'
  if (entry?.kind === 'single' && (entry.provider_outcome_ids || []).length > 1) return 'multiple_singles'
  return 'single'
}

function decimalOddsForOutcome(outcome) {
  return americanToDecimal(outcome?.odds_american)
    ?? Number(outcome?.odds_decimal ?? (Number(outcome?.odds) / 1000))
}

export function estimateParlayPrice(entry, outcomes = {}) {
  if (!['parlay', 'same_game_parlay'].includes(entry?.kind)) return null

  const outcomeIds = entry.provider_outcome_ids || []
  if (outcomeIds.length < 2) return null

  const decimal = combinedDecimalOdds(outcomeIds.map((outcomeId) => decimalOddsForOutcome(outcomes[String(outcomeId)])))
  if (decimal === null) return null

  const american = decimal >= 2
    ? Math.floor((decimal - 1) * 100)
    : Math.floor(-100 / (decimal - 1))
  return {
    decimal: decimal.toFixed(2),
    american: formatAmericanOdds(american),
  }
}


function nextWagerEntryID(entries = []) {
  const ids = new Set(entries.map((entry) => entry.id))
  let index = entries.length + 1
  while (ids.has(`entry:authored:${index}`)) index += 1
  return `entry:authored:${index}`
}

export function createAuthoredWagerEntry(type, outcomeIds, entries = []) {
  const option = wagerType(type)
  return {
    id: nextWagerEntryID(entries),
    kind: option.kind,
    coupon_type: option.couponType,
    provider_outcome_ids: outcomeIds.map(String),
    stakes: [],
  }
}

function isGeneratedDefaultWager(entry) {
  if (entry?.id === 'entry:default:parlay') return true
  if (!entry?.id?.startsWith('entry:single:')) return false
  const outcomeIds = (entry.provider_outcome_ids || []).map(String)
  return entry.kind === 'single'
    && entry.coupon_type === 'single'
    && outcomeIds.length === 1
    && entry.id === `entry:single:${outcomeIds[0]}`
    && !(entry.stakes || []).length
}

export function reconcileAuthoredWagerEntries(entries = [], outcomeIds = [], { preferParlay = false } = {}) {
  const selected = outcomeIds.map(String)
  const selectedSet = new Set(selected)
  if (preferParlay && entries.every(isGeneratedDefaultWager)) {
    if (selected.length === 0) return []
    if (selected.length === 1) {
      return [{
        id: `entry:single:${selected[0]}`,
        kind: 'single',
        coupon_type: 'single',
        provider_outcome_ids: selected,
        stakes: [],
      }]
    }
    return [{
      id: 'entry:default:parlay',
      kind: 'parlay',
      coupon_type: 'combination',
      provider_outcome_ids: selected,
      stakes: [],
    }]
  }
  const reconciled = entries
    .map((entry) => ({
      ...entry,
      provider_outcome_ids: (entry.provider_outcome_ids || []).map(String).filter((outcomeId) => selectedSet.has(outcomeId)),
      stakes: Array.isArray(entry.stakes) ? entry.stakes : [],
    }))
    .filter((entry) => entry.provider_outcome_ids.length > 0)
  const covered = new Set(reconciled.flatMap((entry) => entry.provider_outcome_ids))
  for (const outcomeId of selected) {
    if (covered.has(outcomeId)) continue
    reconciled.push({
      id: `entry:single:${outcomeId}`,
      kind: 'single',
      coupon_type: 'single',
      provider_outcome_ids: [outcomeId],
      stakes: [],
    })
  }
  return reconciled
}

export function applyValidatedDefaultWagerType(entries = [], outcomes = {}) {
  if (entries.length !== 1) return entries

  const entry = entries[0]
  const outcomeIds = (entry.provider_outcome_ids || []).map(String)
  if (entry.id !== 'entry:default:parlay' || outcomeIds.length < 2) return entries

  const eventKeys = outcomeIds.map((outcomeId) => {
    const outcome = outcomes[String(outcomeId)]
    return String(outcome?.event_id || outcome?.event_name || '').trim()
  })
  if (eventKeys.some((eventKey) => !eventKey)) return entries

  const kind = new Set(eventKeys).size === 1 ? 'same_game_parlay' : 'parlay'
  if (entry.kind === kind && entry.coupon_type === 'combination') return entries

  return [{
    ...entry,
    kind,
    coupon_type: 'combination',
  }]
}

export function changeAuthoredWagerType(entry, type) {
  const option = wagerType(type)
  return {
    ...entry,
    id: isGeneratedDefaultWager(entry) ? `entry:authored:${entry.id}` : entry.id,
    kind: option.kind,
    coupon_type: option.couponType,
    stakes: [],
  }
}

export function buildAuthoredWagerPreviewSelectionSet(entries = [], legs = []) {
  if (!entries.length || !legs.length) return null
  const selections = legs.map((leg, index) => {
    const outcomeId = String(leg.selection?.outcome_id || leg.provider_payload?.outcome_id || '')
    return {
      id: `selection:${outcomeId || index + 1}`,
      sort_order: index,
      label: leg.label || leg.selection?.name || `Outcome ${outcomeId}`,
      ...(leg.subtitle ? { subtitle: leg.subtitle } : {}),
      status: 'active',
      odds: leg.selection?.odds || leg.odds || {},
      market: leg.market || {},
      event: leg.event || {},
      provider_ref: {
        key: 'kambi',
        selection_id: outcomeId,
        event_id: String(leg.selection?.event_id || leg.event?.id || ''),
        market_id: String(leg.selection?.bet_offer_id || leg.provider_payload?.bet_offer_id || ''),
        payload: leg.provider_payload || {},
      },
    }
  })
  const selectionIDByOutcome = new Map(selections.map((selection) => [selection.provider_ref.selection_id, selection.id]))
  const previewEntries = entries.map((entry) => {
    const outcomeIds = (entry.provider_outcome_ids || []).map(String)
    const action = {
      type: 'betslip',
      provider_key: 'kambi',
      coupon_type: entry.coupon_type,
      outcome_ids: outcomeIds,
      ...(entry.stakes?.length ? { payload: { stakes: entry.stakes } } : {}),
    }
    return {
      id: entry.id,
      kind: entry.kind,
      label: entry.label || wagerType(authoredWagerType(entry)).label,
      selection_ids: outcomeIds.map((outcomeId) => selectionIDByOutcome.get(outcomeId)).filter(Boolean),
      ...(entry.kind === 'same_game_parlay' ? { group_ids: [`group:${entry.id}`] } : {}),
      provider_key: 'kambi',
      coupon_type: entry.coupon_type,
      outcome_ids: outcomeIds,
      ...(entry.stakes?.length ? { stakes: entry.stakes } : {}),
      action,
    }
  })
  const selectionByID = new Map(selections.map((selection) => [selection.id, selection]))
  const previewGroups = previewEntries
    .filter((entry) => entry.kind === 'same_game_parlay')
    .map((entry) => ({
      id: `group:${entry.id}`,
      kind: 'same_game',
      label: selectionByID.get(entry.selection_ids[0])?.event?.name || 'Same game',
      selection_ids: entry.selection_ids,
      relationship: 'same_event',
    }))
  const summaryKind = previewEntries.length === 1 ? previewEntries[0].kind : 'mixed'
  return {
    schema_version: '2.0',
    source: { provider_key: 'kambi', provider_name: 'Kambi' },
    summary: {
      kind: summaryKind,
      label: previewEntries.length === 1 ? previewEntries[0].label : `${previewEntries.length} wagers`,
      selection_count: selections.length,
      status: 'active',
    },
    selections,
    groups: previewGroups,
    entries: previewEntries,
  }
}

export function validateAuthoredWagerEntries(entries = [], outcomeIds = [], outcomeDetails = {}) {
  if (!outcomeIds.length) return entries.length ? 'Remove wager entries before removing every outcome.' : ''
  if (!entries.length) return 'Define at least one wager entry.'
  const selected = new Set(outcomeIds.map(String))
  const covered = new Set()
  for (const [index, entry] of entries.entries()) {
    const option = wagerType(authoredWagerType(entry))
    const entryOutcomes = (entry.provider_outcome_ids || []).map(String)
    if (!entryOutcomes.length) return `Wager ${index + 1} needs at least one outcome.`
    if (entryOutcomes.some((outcomeId) => !selected.has(outcomeId))) return `Wager ${index + 1} contains an outcome that is no longer selected.`
    if (option.outcomes && entryOutcomes.length !== option.outcomes) {
      return `${option.label} requires exactly ${option.outcomes} outcome${option.outcomes === 1 ? '' : 's'}.`
    }
    if (option.minimumOutcomes && entryOutcomes.length < option.minimumOutcomes) {
      return `${option.label} requires at least ${option.minimumOutcomes} outcomes.`
    }
    if (entry.kind === 'same_game_parlay') {
      const eventKeys = entryOutcomes
        .map((outcomeId) => outcomeDetails?.[outcomeId]?.event_id || outcomeDetails?.[outcomeId]?.event_name)
        .filter(Boolean)
      if (eventKeys.length === entryOutcomes.length && new Set(eventKeys).size > 1) {
        return 'A same game parlay can only include outcomes from one event.'
      }
    }
    for (const outcomeId of entryOutcomes) covered.add(outcomeId)
    if ((entry.stakes || []).some((stake) => !Number.isInteger(stake) || stake <= 0)) {
      return `Wager ${index + 1} has an invalid stake.`
    }
  }
  const missing = [...selected].filter((outcomeId) => !covered.has(outcomeId))
  return missing.length ? `Add outcome${missing.length === 1 ? '' : 's'} ${missing.join(', ')} to a wager.` : ''
}


export function createPostComposerState(skipApproval = false) {
  return {
    userId: '',
    text: '',
    title: '',
    subtitle: '',
    selectionIds: [],
    mediaObjects: [],
    ctaKind: 'betslip',
    ctaLabel: '',
    ctaTarget: '',
    score: 1,
    isHidden: false,
    isPinned: false,
    pinnedUntil: '',
    boostFactor: 1,
    surfaceIds: null,
    skipApproval,
    kambiOutcomeIds: [],
    wagerEntries: [],
    providerSelectionIds: [],
    targetOfferings: [],
    tagIds: [],
    postType: 'organic',
    expiresAt: '',
    archiveOnCompletion: false,
    scheduledFor: '',
    promoPriority: 0,
    interleaveEvery: '',
    placementMode: 'pinned',
    shareEnabled: true,
    copyCountEnabled: null,
    cardPresentation: null,
  }
}

export function normalizePostComposerState(savedState = {}) {
  const defaults = createPostComposerState(Boolean(savedState.skipApproval))
  return {
    ...defaults,
    ...savedState,
    selectionIds: savedState.selectionIds || [],
    mediaObjects: savedState.mediaObjects || [],
    kambiOutcomeIds: savedState.kambiOutcomeIds || [],
    providerSelectionIds: savedState.providerSelectionIds || [],
    wagerEntries: Array.isArray(savedState.wagerEntries) ? savedState.wagerEntries : [],
    targetOfferings: savedState.targetOfferings || [],
    tagIds: savedState.tagIds || [],
    surfaceIds: Array.isArray(savedState.surfaceIds) ? savedState.surfaceIds : null,
  }
}

export const POST_COMPOSER_DOCUMENT_SCHEMA_VERSION = 1

export const SUGGESTED_BETS_MAX_AGE_MS = 24 * 60 * 60 * 1000

export function createComposerAiReview() {
  // What a bet run read without a match, and its warnings, are for the run
  // just made: a restored draft starts without them (#870).
  return { caption: '', captions: [], tags: [], betSummary: null, transcript: '', suggestedBets: [], suggestedBetsUnmatched: [], suggestedBetsWarnings: [], suggestedBetsSource: '', generatedAt: null }
}

export function normalizeComposerAiReview(savedReview = {}) {
  const generatedAt = typeof savedReview.generatedAt === 'string' ? savedReview.generatedAt : null
  const generatedTime = generatedAt ? Date.parse(generatedAt) : NaN
  const suggestionsAreFresh = Number.isFinite(generatedTime)
    && Date.now() - generatedTime <= SUGGESTED_BETS_MAX_AGE_MS
  const suggestedBets = suggestionsAreFresh && Array.isArray(savedReview.suggestedBets)
    ? savedReview.suggestedBets.filter((suggestion) => suggestion?.result?.selection_ref?.external_outcome_id)
    : []
  return {
    ...createComposerAiReview(),
    caption: typeof savedReview.caption === 'string' ? savedReview.caption : '',
    captions: Array.isArray(savedReview.captions) ? savedReview.captions.filter(Boolean) : [],
    tags: Array.isArray(savedReview.tags) ? savedReview.tags.filter(Boolean) : [],
    betSummary: savedReview.betSummary || null,
    transcript: typeof savedReview.transcript === 'string' ? savedReview.transcript : '',
    suggestedBets,
    suggestedBetsSource: suggestedBets.length && typeof savedReview.suggestedBetsSource === 'string' ? savedReview.suggestedBetsSource : '',
    generatedAt: suggestedBets.length ? generatedAt : null,
  }
}

// The adjunct half of the composer document: AI-assisted authoring state that
// rides alongside the authored post fields but never reaches the create/update
// API payload directly. It still needs schema-versioned normalization so a
// draft saved before a field existed restores to a safe default instead of
// `undefined` leaking into a controlled input.
export function createComposerAdjunctState() {
  return {
    aiReview: createComposerAiReview(),
    imagePrompt: '',
    imagePromptFields: EMPTY_AI_PROMPT_FIELDS,
    selectedPromptPresetId: '',
    referenceImages: [],
    imageGenerationJob: null,
    generatedImageReview: null,
    revisionPrompt: '',
    transcriptionJobs: {},
    generatedAssetIds: [],
  }
}

// AI reference images are uploaded assets tagged with the organization they
// were uploaded under; anything without that shape is dropped on restore.
export function normalizeComposerReferenceImages(referenceImages) {
  if (!Array.isArray(referenceImages)) return []
  return referenceImages
    .filter((reference) => (
      typeof reference?.id === 'string'
      && typeof reference.url === 'string'
      && typeof reference.orgSlug === 'string'
    ))
    .map((reference) => ({
      id: reference.id,
      url: reference.url,
      title: typeof reference.title === 'string' ? reference.title : '',
      orgSlug: reference.orgSlug,
    }))
}

export function normalizeComposerAdjunctState(savedAdjunct = {}) {
  return {
    ...createComposerAdjunctState(),
    aiReview: normalizeComposerAiReview(savedAdjunct.aiReview),
    imagePrompt: typeof savedAdjunct.imagePrompt === 'string' ? savedAdjunct.imagePrompt : '',
    imagePromptFields: normalizeAiPromptFields(savedAdjunct.imagePromptFields || {}),
    selectedPromptPresetId: savedAdjunct.selectedPromptPresetId || '',
    referenceImages: normalizeComposerReferenceImages(savedAdjunct.referenceImages),
    imageGenerationJob: savedAdjunct.imageGenerationJob || null,
    generatedImageReview: savedAdjunct.generatedImageReview || null,
    revisionPrompt: typeof savedAdjunct.revisionPrompt === 'string' ? savedAdjunct.revisionPrompt : '',
    transcriptionJobs: savedAdjunct.transcriptionJobs && typeof savedAdjunct.transcriptionJobs === 'object'
      ? savedAdjunct.transcriptionJobs
      : {},
    generatedAssetIds: Array.isArray(savedAdjunct.generatedAssetIds) ? savedAdjunct.generatedAssetIds : [],
  }
}

// Whether the adjunct half alone justifies treating the composer as
// in-progress (draft banner, unsaved-changes guard). Caption/tag suggestions
// are excluded on purpose: they mirror content already covered by the
// authored-field check and would otherwise arm the guard on read-only AI
// output the operator never acted on.
export function isComposerAdjunctInProgress(adjunct = createComposerAdjunctState()) {
  return Boolean(
    adjunct.imagePrompt?.trim()
    || Object.values(adjunct.imagePromptFields || {}).some((value) => String(value || '').trim())
    || adjunct.selectedPromptPresetId
    || (adjunct.referenceImages || []).length > 0
    || adjunct.generatedImageReview
    || canResumeImageGenerationJob(adjunct.imageGenerationJob)
    || adjunct.revisionPrompt?.trim()
    || Object.keys(adjunct.transcriptionJobs || {}).length > 0
    || (adjunct.generatedAssetIds || []).length > 0
  )
}

export function createComposerDocument({ authored, skipApproval = false } = {}) {
  return {
    schemaVersion: POST_COMPOSER_DOCUMENT_SCHEMA_VERSION,
    authored: authored || createPostComposerState(skipApproval),
    adjunct: createComposerAdjunctState(),
  }
}

export function normalizeComposerDocument(savedDocument = {}, { normalizeAuthored = normalizePostComposerState } = {}) {
  return {
    schemaVersion: POST_COMPOSER_DOCUMENT_SCHEMA_VERSION,
    authored: normalizeAuthored(savedDocument.authored ?? savedDocument),
    adjunct: normalizeComposerAdjunctState(savedDocument.adjunct ?? savedDocument),
  }
}

// The composer controller: one versioned document (authored fields + AI/media
// adjunct state) backing both the individual-book and Command Center
// composers. `createAuthored`/`normalizeAuthored` let each consumer plug in
// its own authored shape (Command Center's carries per-book maps) without a
// second document convention. The returned document is named
// `composerDocument`, not `document` -- both consumers key DOM lookups off
// the global `document` and a same-named local would shadow it.
export function useComposerDocument({
  createAuthored = () => createPostComposerState(false),
  normalizeAuthored = normalizePostComposerState,
} = {}) {
  const [composerDocument, setComposerDocument] = useState(
    () => createComposerDocument({ authored: createAuthored() })
  )

  const setAuthored = useCallback((updater) => {
    setComposerDocument((current) => ({
      ...current,
      authored: typeof updater === 'function' ? updater(current.authored) : updater,
    }))
  }, [])

  const setAdjunctField = useCallback((field, updater) => {
    setComposerDocument((current) => ({
      ...current,
      adjunct: {
        ...current.adjunct,
        [field]: typeof updater === 'function' ? updater(current.adjunct[field]) : updater,
      },
    }))
  }, [])

  const replaceDocument = useCallback((next) => {
    setComposerDocument((current) => normalizeComposerDocument(
      typeof next === 'function' ? next(current) : next,
      { normalizeAuthored },
    ))
  }, [normalizeAuthored])

  return {
    composerDocument,
    authored: composerDocument.authored,
    adjunct: composerDocument.adjunct,
    setAuthored,
    setAdjunctField,
    replaceDocument,
  }
}

export function serializePostComposerMedia(mediaObjects = []) {
  return mediaObjects.map((media, index) => ({
    id: media.mediaObjectId || media.id,
    asset_id: media.mediaId || undefined,
    object_key: media.object_key,
    source_object_key: media.source_object_key,
    type: media.type,
    url: media.url,
    sort_order: index,
    // Only a video carries captions, and only when the composer manages
    // them: a missing key keeps the tracks stored for this media object.
    ...(media.type === 'video' && Array.isArray(media.captions) ? { captions: media.captions } : {}),
  }))
}

function isoOrNull(value) {
  return value ? new Date(value).toISOString() : null
}

// datetime-local inputs take local wall time, not a UTC timestamp.
export function formatDateTimeInput(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return localDate.toISOString().slice(0, 16)
}

function interleaveValue(formData) {
  return formData.placementMode === 'interleave' && formData.interleaveEvery
    ? parseInt(formData.interleaveEvery, 10)
    : null
}

function applyProviderSelection(payload, formData, providerKind, {
  allowPartialRegulations = false,
  requestedTargetOfferings = null,
} = {}) {
  if (providerKind === 'kambi') {
    // Regulations are a distribution choice, not a property of the bet: a
    // content-only post still decides where it runs, so they serialize
    // independently of whether an outcome is attached.
    //
    // Always send the array, including when empty. The update handler keys off
    // nil vs empty (post_service.go: `req.TargetOfferings != nil`), so omitting
    // it on an empty selection leaves the stored regulations untouched and
    // turns Clear into a silent no-op.
    payload.target_offerings = formData.targetOfferings

    if (formData.kambiOutcomeIds.length > 0) {
      payload.kambi_outcome_ids = formData.kambiOutcomeIds
      payload.wager_entries = formData.wagerEntries || []
      delete payload.selection_ids

      // Resubmit the original regulations for the server to re-check; the
      // form's narrower selection is the list the operator confirmed.
      if (allowPartialRegulations) {
        payload.target_offerings = requestedTargetOfferings ?? formData.targetOfferings
        payload.allow_partial_regulations = true
        payload.confirmed_target_offerings = formData.targetOfferings
      }
    }
  }

  if (PREDICTION_PROVIDERS.has(providerKind) && formData.providerSelectionIds.length > 0) {
    payload.provider_selection_ids = formData.providerSelectionIds
    delete payload.selection_ids
  }

  return payload
}

export function serializePostComposerCTA(formData, { persistDefault = false } = {}) {
  if (!formData.ctaKind) return null
  if (formData.ctaKind === 'betslip') {
    return persistDefault ? { kind: 'betslip', label: '', target: '' } : null
  }
  if (formData.ctaKind === 'none') return { kind: 'none', label: '', target: '' }
  return {
    kind: formData.ctaKind,
    label: formData.ctaLabel,
    target: formData.ctaTarget,
  }
}

export function isPostComposerCopyCountEligible(formData) {
  const hasBetLegs = (
    (formData.selectionIds?.length || 0)
    + (formData.kambiOutcomeIds?.length || 0)
    + (formData.providerSelectionIds?.length || 0)
    + (formData.wagerEntries?.length || 0)
  ) > 0
  return hasBetLegs && (formData.ctaKind === 'betslip' || formData.ctaKind === 'provider-cart')
}

export function resolvePostComposerCopyCountEnabled(formData) {
  return isPostComposerCopyCountEligible(formData) && formData.copyCountEnabled !== false
}

export function buildFeedCreatePayload(formData, {
  additionalOrganizationIds = [],
  providerKind = 'generic',
  allowPartialRegulations = false,
  requestedTargetOfferings = null,
} = {}) {
  const payload = {
    creator_id: formData.userId,
    text: formData.text,
    title: formData.title,
    subtitle: formData.subtitle,
    selection_ids: formData.selectionIds,
    media_objects: serializePostComposerMedia(formData.mediaObjects),
    score: formData.score,
    is_hidden: formData.isHidden,
    is_pinned: formData.isPinned,
    pinned_until: isoOrNull(formData.pinnedUntil),
    boost_factor: formData.boostFactor,
    cta: serializePostComposerCTA(formData),
    additional_organization_ids: additionalOrganizationIds.length > 0 ? additionalOrganizationIds : undefined,
    skip_approval: formData.skipApproval || undefined,
    tag_ids: formData.tagIds.length > 0 ? formData.tagIds : undefined,
    surface_ids: Array.isArray(formData.surfaceIds) ? formData.surfaceIds : null,
    post_type: formData.postType,
    expires_at: isoOrNull(formData.expiresAt),
    archive_on_completion: Boolean(formData.archiveOnCompletion),
    promo_priority: formData.promoPriority,
    interleave_every: interleaveValue(formData),
    scheduled_for: isoOrNull(formData.scheduledFor),
    share_enabled: formData.shareEnabled !== false,
    copy_count_enabled: resolvePostComposerCopyCountEnabled(formData),
  }
  if (formData.cardPresentation) {
    payload.card_presentation = formData.cardPresentation
  }

  return applyProviderSelection(payload, formData, providerKind, { allowPartialRegulations, requestedTargetOfferings })
}
function providerTechnicalContract(formData) {
  return {
    selectionIds: formData.selectionIds || [],
    kambiOutcomeIds: formData.kambiOutcomeIds || [],
    providerSelectionIds: formData.providerSelectionIds || [],
    wagerEntries: formData.wagerEntries || [],
    targetOfferings: formData.targetOfferings || [],
    cta: serializePostComposerCTA(formData, { persistDefault: true }),
  }
}

function hasProviderTechnicalContractChanges(formData, baselineFormData) {
  if (!baselineFormData) return true
  return JSON.stringify(providerTechnicalContract(formData))
    !== JSON.stringify(providerTechnicalContract(baselineFormData))
}

function buildFeedEditorialUpdatePayload(formData) {
  return {
    text: formData.text,
    title: formData.title,
    subtitle: formData.subtitle,
    score: formData.score,
    is_hidden: formData.isHidden,
    is_pinned: formData.isPinned,
    pinned_until: isoOrNull(formData.pinnedUntil),
    boost_factor: formData.boostFactor,
    media_objects: serializePostComposerMedia(formData.mediaObjects),
    tag_ids: formData.tagIds,
    surface_ids: Array.isArray(formData.surfaceIds) ? formData.surfaceIds : undefined,
    post_type: formData.postType,
    expires_at: isoOrNull(formData.expiresAt),
    archive_on_completion: Boolean(formData.archiveOnCompletion),
    promo_priority: formData.promoPriority,
    interleave_every: interleaveValue(formData),
    scheduled_for: isoOrNull(formData.scheduledFor),
    share_enabled: formData.shareEnabled !== false,
    copy_count_enabled: resolvePostComposerCopyCountEnabled(formData),
    card_presentation: formData.cardPresentation,
  }
}

function omitUnchangedUpdateFields(payload, baselinePayload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => (
      JSON.stringify(value) !== JSON.stringify(baselinePayload[key])
    )),
  )
}

export function buildFeedUpdatePayload(formData, {
  providerKind = 'generic',
  baselineFormData = null,
  allowPartialRegulations = false,
  requestedTargetOfferings = null,
} = {}) {
  const currentPayload = buildFeedEditorialUpdatePayload(formData)
  const payload = baselineFormData
    ? omitUnchangedUpdateFields(currentPayload, buildFeedEditorialUpdatePayload(baselineFormData))
    : currentPayload

  if (!hasProviderTechnicalContractChanges(formData, baselineFormData)) {
    return payload
  }

  const cta = serializePostComposerCTA(formData, { persistDefault: true })
  payload.cta_kind = cta?.kind ?? null
  payload.cta_label = cta?.label ?? null
  payload.cta_target = cta?.target ?? null
  payload.selection_ids = formData.selectionIds
  return applyProviderSelection(payload, formData, providerKind, { allowPartialRegulations, requestedTargetOfferings })
}

export function getPostComposerProviderKind({ isKambi = false, isCryptoDotCom = false, isOG = false } = {}) {
  if (isKambi) return 'kambi'
  if (isCryptoDotCom) return 'cryptodotcom'
  if (isOG) return 'og'
  return 'generic'
}

export function validatePostComposerStep(step, formData, {
  hasEnabledSurfaces = true,
  marketValidationError = '',
} = {}) {
  if (step === 'distribution' && !hasEnabledSurfaces) {
    return { step, message: 'Configure at least one feed surface before continuing.' }
  }
  if (step === 'market' && marketValidationError) {
    return { step, message: marketValidationError }
  }
  if (step === 'submit' && !hasEnabledSurfaces) {
    return { step: 'distribution', message: 'Configure at least one feed surface before publishing.' }
  }
  return null
}
